import {
  FC,
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { Popconfirm } from "antd";
import {
  Type,
  Image as ImageIcon,
  ImageMinus,
  Images,
  ScanFace,
  Cpu,
  Scan,
  Clock3,
  RectangleHorizontal,
  ChevronDown,
  ImageUp,
  ImageDown,
  Film,
  AudioLines,
  Coins,
  ArrowRight,
  Loader2,
  Play,
  Sparkles,
  ArrowUpRight,
  Eye,
  Clapperboard,
  Trash2,
  AlertCircle,
  Download,
  MoreHorizontal,
} from "lucide-react";
import "./AiVideo.scss";
import {
  getAiVideoTemplateListApi,
  getAiVideoTaskListApi,
  createAiVideoTaskApi,
  deleteAiVideoTaskApi,
  previewAiVideoPointApi,
  extractList,
} from "../../service/aiVideo";
import { VIDEO_CDN_PREFIX } from "../../config/video";
import { downloadFileSmart } from "../../utils/batchVideoDownload";
import { AppToast } from "../../components/common/AlertMessage";
// 积分预估图标（与音色设计 voice-design-ws__cost-estimate 保持一致）
import { CoinsEstimateIcon } from "../../components/common/CoinsEstimateIcon";

// ====== 类型定义 ======
type Mode = "text" | "first" | "last" | "first_last" | "reference";
type SelectKey = "model" | "resolution" | "duration" | "ratio";
type GalleryTab = "templates" | "works";
type TemplateFilter =
  | "all"
  | "cinematic"
  | "landscape"
  | "portrait"
  | "product";
type FileKey =
  | "first"
  | "last"
  | "referenceImage"
  | "referenceVideo"
  | "referenceAudio";

/** 前端 Mode → 后端 generationMode 映射 */
const MODE_TO_API: Record<Mode, AiVideo.GenerationMode> = {
  text: "TEXT_TO_VIDEO",
  first: "FIRST_FRAME",
  last: "LAST_FRAME",
  first_last: "FIRST_LAST_FRAME",
  reference: "REFERENCE",
};

/** 后端 generationMode → 前端 Mode 映射 */
const API_TO_MODE: Record<string, Mode> = {
  TEXT_TO_VIDEO: "text",
  FIRST_FRAME: "first",
  LAST_FRAME: "last",
  FIRST_LAST_FRAME: "first_last",
  REFERENCE: "reference",
};

/** 模板分类中文名映射（后端返回中文 → 前端枚举） */
const CATEGORY_MAP: Record<string, Exclude<TemplateFilter, "all">> = {
  风光: "landscape",
  自然: "landscape",
  自然风光: "landscape",
  电影: "cinematic",
  电影叙事: "cinematic",
  人物: "portrait",
  人物短片: "portrait",
  产品: "product",
  产品广告: "product",
};

/** 将后端素材 URL 解析为完整可访问地址（清理反引号/引号包裹） */
const resolveMediaUrl = (rawUrl?: string): string => {
  if (!rawUrl) return "";
  // 清理后端可能返回的反引号/引号包裹（如 "`http://...`"）
  const cleaned = rawUrl.trim().replace(/^["'`]+|["'`]+$/g, "");
  if (!cleaned) return "";
  if (cleaned.startsWith("http://") || cleaned.startsWith("https://"))
    return cleaned;
  const path = cleaned.startsWith("/") ? cleaned : `/${cleaned}`;
  return `${VIDEO_CDN_PREFIX}${path}`;
};

/** 模板列数（对齐 28.AI视频.html 的 masonry 三列布局） */
const TEMPLATE_COLUMN_COUNT = 3;

/**
 * 计算模板卡片的高度分数（用于瀑布流列分配）
 * 分数越高的卡片占用列空间越多，会被分配到当前最矮列以均衡各列高度
 * - 横屏比例（21:9/16:9）分数低，竖屏比例（9:16）分数高
 * - adaptive 比例视频真实宽高比未知，按 1:1 中间值估算
 *   避免误判为 16:9 导致竖屏视频实际渲染高度很高但分数偏低，
 *   被分配到已较高列造成列高度失衡与底部大空白
 * - 纳入 card-body 固定高度（标题+meta+padding）折算为比例偏移
 *   让分数更贴近卡片真实渲染高度，提升分配均衡度
 */
const templateHeightScore = (ratio: string): number => {
  // adaptive 比例按 1:1 中间值估算（视频可能是任意比例，1:1 是横竖屏的中间值）
  const safeRatio = !ratio || ratio === "adaptive" ? "1:1" : ratio;
  const parts = safeRatio.split(":").map(Number);
  const w = parts[0] || 16;
  const h = parts[1] || 9;
  // card-body 固定高度折算：假设列宽 ~340px，card-body ~62px，折算 ~0.18
  const bodyOffset = 0.18;
  return h / w + bodyOffset;
};

/** 视频模板项（来自接口） */
interface TemplateItem {
  templateId: string;
  title: string;
  category: Exclude<TemplateFilter, "all">;
  ratio: string;
  prompt: string;
  poster: string;
  duration: number;
  meta: string[];
  overlayLabel: string;
  videoUrl?: string;
  /** 原始模板数据，用于"做同款"载入完整参数 */
  raw?: AiVideo.TemplateRespVO;
}

/** 灵感卡片项 */
interface IdeaItem {
  icon: "mountain" | "umbrella" | "watch";
  duration: string;
  title: string;
  summary: string;
  prompt: string;
}

/** 已生成 / 生成中的视频作品项 */
interface WorkItem {
  id: string;
  taskId: number;
  mode: Mode;
  modeLabel: string;
  prompt: string;
  model: string;
  resolution: string;
  duration: number;
  ratio: string;
  poster: string;
  videoUrl?: string;
  pending: boolean;
  status?: AiVideo.TaskStatus;
  /** 失败原因（status=failed 时由后端返回） */
  errorMessage?: string;
  /** 后端返回的输入素材（首帧/尾帧/参考图等 URL），用于"再次编辑"回填 */
  inputFiles?: AiVideo.InputFile[];
  createdAt: string;
}

/** 上传文件状态 */
interface FilesState {
  first: File[];
  last: File[];
  referenceImage: File[];
  referenceVideo: File[];
  referenceAudio: File[];
}

/**
 * 远程素材项（来自后端 inputFiles，用于"再次编辑"时回填预览）
 * 与 File 并存于 MediaSlot 中，提交时通过 fetch 转 File 再上传
 */
interface RemoteFile {
  url: string;
  originalName?: string;
}

/** 远程素材分组状态，与 FilesState 一一对应 */
interface RemoteFilesState {
  first: RemoteFile[];
  last: RemoteFile[];
  referenceImage: RemoteFile[];
  referenceVideo: RemoteFile[];
  referenceAudio: RemoteFile[];
}

/**
 * 将后端 inputFiles 按 usage/category 分类到 RemoteFilesState
 * 兼容 FirstFrame / LastFrame / Image / Video / Audio 等 usage 值
 */
const categorizeInputFiles = (
  inputs?: AiVideo.InputFile[],
): RemoteFilesState => {
  const empty: RemoteFilesState = {
    first: [],
    last: [],
    referenceImage: [],
    referenceVideo: [],
    referenceAudio: [],
  };
  if (!inputs || inputs.length === 0) return empty;
  // 视频扩展名兜底识别（后端 category/usage 字段不规范时通过文件名判断）
  const videoExtRegex = /\.(mp4|mov|webm|mkv|avi|m4v)(\?|$)/i;
  const audioExtRegex = /\.(mp3|wav|m4a|aac|ogg|flac)(\?|$)/i;
  inputs.forEach((f) => {
    // 后端返回的素材 URL 可能是 http，提交给后端时需统一为 https 避免报错
    const raw = resolveMediaUrl(f.url);
    const url = raw ? raw.replace(/^http:\/\//i, "https://") : "";
    if (!url) return;
    const item: RemoteFile = { url, originalName: f.originalName };
    const usage = (f.usage || "").toUpperCase();
    const category = (f.category || "").toUpperCase();
    // 优先按 usage/category 字段识别首尾帧
    if (
      usage === "FIRSTFRAME" ||
      usage === "FIRST_FRAME" ||
      usage === "FIRST"
    ) {
      empty.first.push(item);
    } else if (
      usage === "LASTFRAME" ||
      usage === "LAST_FRAME" ||
      usage === "LAST"
    ) {
      empty.last.push(item);
    } else if (category === "VIDEO" || usage === "VIDEO") {
      empty.referenceVideo.push(item);
    } else if (category === "AUDIO" || usage === "AUDIO") {
      empty.referenceAudio.push(item);
    } else if (
      videoExtRegex.test(url) ||
      videoExtRegex.test(item.originalName || "")
    ) {
      // 字段未声明但 URL/文件名是视频扩展名：兜底归入参考视频
      empty.referenceVideo.push(item);
    } else if (
      audioExtRegex.test(url) ||
      audioExtRegex.test(item.originalName || "")
    ) {
      // 字段未声明但 URL/文件名是音频扩展名：兜底归入参考音频
      empty.referenceAudio.push(item);
    } else {
      // 默认归入参考图片（category === "Image" 或其他）
      empty.referenceImage.push(item);
    }
  });
  return empty;
};

/**
 * 将远程 URL 异步转换为 File 对象
 * 用于提交任务前把"再次编辑"回填的远程素材转为可上传的 File
 */
const fetchUrlToFile = async (
  url: string,
  originalName?: string,
): Promise<File | null> => {
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) return null;
    const blob = await res.blob();
    // 文件名兜底：从 URL 提取或用时间戳
    const name =
      originalName ||
      url.split("/").pop()?.split("?")[0] ||
      `file-${Date.now()}`;
    return new File([blob], name, { type: blob.type || "image/*" });
  } catch {
    return null;
  }
};

/** 下拉选择器选项 */
interface SelectOption {
  value: string;
  label: string;
  hidden?: boolean;
}

// ====== 常量数据（写死，不调用接口）======

/**
 * 模型 / 分辨率 / 时长选择器选项（与组件状态无关，上提到模块级避免每次渲染重建）
 * 早期版本用 useMemo([],) 包裹，但依赖为空数组，等价于模块级常量，直接上提即可
 */
const MODEL_OPTIONS: SelectOption[] = [{ value: "MiniMax-H3", label: "MiniMax-H3" }];
const RESOLUTION_OPTIONS: SelectOption[] = [
  { value: "768P", label: "768P" },
  { value: "1080P", label: "1080P" },
  { value: "2K", label: "2K" },
  { value: "4K", label: "4K" },
];
const DURATION_OPTIONS: SelectOption[] = Array.from({ length: 12 }, (_, i) => {
  const value = String(i + 4);
  return { value, label: `${value} 秒` };
});

/** 模式文案映射 */
const MODE_LABELS: Record<Mode, string> = {
  text: "文生视频",
  first: "首帧生视频",
  last: "尾帧生视频",
  first_last: "首尾帧生视频",
  reference: "多模态参考",
};

/** 模式切换按钮配置 */
const MODE_TABS: Array<{ mode: Mode; icon: ReactNode; label: string }> = [
  { mode: "text", icon: <Type />, label: "文生视频" },
  { mode: "first", icon: <ImageIcon />, label: "首帧生视频" },
  { mode: "last", icon: <ImageMinus />, label: "尾帧生视频" },
  { mode: "first_last", icon: <Images />, label: "首尾帧" },
  { mode: "reference", icon: <ScanFace />, label: "多模态参考" },
];

/** 灵感卡片数据 */
const IDEA_CARDS: IdeaItem[] = [
  {
    icon: "mountain",
    duration: "风光 · 航拍",
    title: "云海日出",
    summary: "航拍穿越薄雾，寺庙在晨光中显现。",
    prompt:
      "清晨薄雾中的东方山谷，航拍镜头缓慢掠过云海与层叠山脊，一座古老寺庙在金色日出中渐渐显现，电影级光影，宁静而壮阔。",
  },
  {
    icon: "umbrella",
    duration: "人物 · 运镜",
    title: "霓虹雨夜",
    summary: "低机位跟拍，雨水倒映流动的城市霓虹。",
    prompt:
      "未来都市雨夜，一名银发女孩撑着透明雨伞穿过霓虹街道，镜头从鞋边积水的倒影缓慢抬升至她的侧脸，车辆光轨掠过，赛博朋克电影质感。",
  },
  {
    icon: "watch",
    duration: "产品 · 微距",
    title: "机械之美",
    summary: "微距光线扫过精密材质与齿轮细节。",
    prompt:
      "极简黑色背景中，一只精密机械腕表从暗处缓慢旋转进入光线，微距镜头扫过金属拉丝、表冠与齿轮结构，冷白轮廓光，奢华产品广告质感。",
  },
];

/** 视频模板数据（12 个） */
const TEMPLATE_CARDS: TemplateItem[] = [];

/** 模板分类筛选配置 */
const TEMPLATE_FILTERS: Array<{ key: TemplateFilter; label: string }> = [
  { key: "all", label: "全部" },
  { key: "cinematic", label: "电影叙事" },
  { key: "landscape", label: "自然风光" },
  { key: "portrait", label: "人物短片" },
  { key: "product", label: "产品广告" },
];

/** 后端分类文本 → 前端展示标签 */
const CATEGORY_LABEL: Record<Exclude<TemplateFilter, "all">, string> = {
  cinematic: "电影叙事",
  landscape: "自然风光",
  portrait: "人物短片",
  product: "产品广告",
};

/**
 * 将后端 referenceType（如 "TEXT"）映射为前端 Mode
 */
const REF_TYPE_TO_MODE: Record<string, Mode> = {
  TEXT: "text",
  FIRST_FRAME: "first",
  LAST_FRAME: "last",
  FIRST_LAST_FRAME: "first_last",
  REFERENCE: "reference",
};

/**
 * 判断任务状态是否为处理中（兼容大小写）
 */
const isPendingStatus = (status?: AiVideo.TaskStatus): boolean => {
  if (!status) return true;
  const upper = status.toUpperCase();
  // queued(排队中) / pending / processing 均视为"正在生成"
  return upper === "PENDING" || upper === "PROCESSING" || upper === "QUEUED";
};

/**
 * 判断任务状态是否为失败（兼容大小写）
 */
const isFailedStatus = (status?: AiVideo.TaskStatus): boolean => {
  if (!status) return false;
  return status.toUpperCase() === "FAILED";
};

/**
 * 判断任务状态是否为成功（兼容大小写）
 */
const isSuccessStatus = (status?: AiVideo.TaskStatus): boolean => {
  if (!status) return false;
  return status.toUpperCase() === "SUCCESS";
};

/**
 * 从 TaskRespVO 中提取媒体地址（视频优先于音频）
 * 兼容 videoUrl / outputVideoUrl / outputAudioUrl
 */
const extractMediaUrl = (t: AiVideo.TaskRespVO): string => {
  return resolveMediaUrl(t.videoUrl || t.outputVideoUrl || t.outputAudioUrl);
};

/**
 * 从 TaskRespVO 中提取提示词文本
 * 兼容 prompt / script
 */
const extractPrompt = (t: AiVideo.TaskRespVO): string => {
  return t.prompt || t.script || "";
};

/**
 * 从 TemplateRespVO 中提取媒体地址
 */
const extractTemplateMediaUrl = (t: AiVideo.TemplateRespVO): string => {
  return resolveMediaUrl(t.videoUrl || t.outputVideoUrl || t.outputAudioUrl);
};

/**
 * 从 TemplateRespVO 中提取提示词文本
 */
const extractTemplatePrompt = (t: AiVideo.TemplateRespVO): string => {
  return t.prompt || t.script || "";
};

/**
 * 从 TemplateRespVO 中提取 ID（兼容 id 和 taskId）
 */
const extractTemplateId = (t: AiVideo.TemplateRespVO): number => {
  return t.id ?? t.taskId ?? 0;
};

/**
 * 将后端模板数据转换为前端展示用的 TemplateItem
 * 缺失字段使用合理默认值，保证渲染不报错
 * 兼容 id 和 taskId 两种字段
 */
const mapTemplateToItem = (t: AiVideo.TemplateRespVO): TemplateItem => {
  const tplId = extractTemplateId(t);
  const category = CATEGORY_MAP[t.category || ""] || "cinematic";
  const ratio = t.aspectRatio || "16:9";
  const duration = t.duration ?? t.originalDuration ?? 5;
  const overlayLabel = CATEGORY_LABEL[category];
  const meta: string[] = [];
  const modeKey = t.generationMode
    ? API_TO_MODE[t.generationMode] || "text"
    : t.referenceType
      ? REF_TYPE_TO_MODE[t.referenceType] || "text"
      : "text";
  if (modeKey !== "text") {
    meta.push(MODE_LABELS[modeKey]);
  }
  meta.push(ratio === "adaptive" ? "自适应" : ratio);
  meta.push(`${duration}秒`);
  return {
    templateId: `tpl-${tplId}`,
    title: t.title || t.description?.slice(0, 20) || `模板 ${tplId}`,
    category,
    ratio,
    prompt: extractTemplatePrompt(t),
    // 兜底封面：接口未返回 poster 时用渐变色占位，避免空白
    poster: "linear-gradient(135deg,#0b1020 0%,#233a64 48%,#e5a760 100%)",
    videoUrl: extractTemplateMediaUrl(t),
    duration,
    meta,
    overlayLabel,
    raw: t,
  };
};

/**
 * 将后端任务数据转换为前端展示用的 WorkItem
 * 兼容文档字段和后端实际返回字段
 */
const mapTaskToWork = (t: AiVideo.TaskRespVO): WorkItem => {
  const mode = t.generationMode
    ? API_TO_MODE[t.generationMode] || "text"
    : t.referenceType
      ? REF_TYPE_TO_MODE[t.referenceType] || "text"
      : "text";
  const status = t.status;
  const pending = isPendingStatus(status);
  const failed = isFailedStatus(status);
  const ratio = t.aspectRatio || "16:9";
  const duration = t.duration ?? t.originalDuration ?? 0;
  return {
    id: `task-${t.taskId}`,
    taskId: t.taskId,
    mode,
    modeLabel: MODE_LABELS[mode],
    prompt: extractPrompt(t),
    model: "MiniMax-H3",
    resolution: t.resolution || "",
    duration,
    ratio,
    // 失败状态使用暗红色兜底封面，其他状态用深蓝渐变
    poster: failed
      ? "linear-gradient(135deg,#1a0b0b 0%,#4a1f24 48%,#7a2d31 100%)"
      : "linear-gradient(135deg,#0b1020 0%,#233a64 48%,#e5a760 100%)",
    videoUrl: extractMediaUrl(t),
    pending,
    status,
    errorMessage: t.errorMessage,
    // 保存后端返回的输入素材 URL，用于"再次编辑/重新生成"回填到上传槽位
    inputFiles: t.inputFiles,
    createdAt: t.createTime || "",
  };
};

/** 默认提示词 */
const DEFAULT_PROMPT = "";

/** 提示词最大字数 */
const PROMPT_MAX_LENGTH = 7000;

/**
 * 合并作品列表：按 taskId 做 diff，仅替换状态有变化的项，未变项保留原引用
 * 避免 30 秒轮询时 setWorks(list.map(mapTaskToWork)) 全量替换新对象引用，
 * 导致所有 WorkCardView 的 memo 失效而重渲染（作品多时性能损耗显著）
 * 比较影响渲染的关键字段：status / pending / videoUrl / errorMessage /
 * prompt / resolution / ratio / duration / inputFiles
 *
 * 防御性保留乐观项：当后端返回列表长度小于 prev 时（如连续提交多个任务时
 * 后端尚未同步入库），保留 prev 中存在但 next 中缺失的 taskId 对应的乐观项，
 * 避免"两个变一个"的闪烁。下次轮询后端同步后会自然补齐。
 */
const mergeWorks = (prev: WorkItem[], next: WorkItem[]): WorkItem[] => {
  if (prev.length === next.length) {
    const prevMap = new Map(prev.map((w) => [w.taskId, w]));
    let changed = false;
    const result = next.map((n) => {
      const p = prevMap.get(n.taskId);
      if (!p) return n;
      if (
        p.status === n.status &&
        p.pending === n.pending &&
        p.videoUrl === n.videoUrl &&
        p.errorMessage === n.errorMessage &&
        p.prompt === n.prompt &&
        p.resolution === n.resolution &&
        p.ratio === n.ratio &&
        p.duration === n.duration &&
        p.inputFiles === n.inputFiles
      ) {
        return p; // 关键字段全相同，保留旧引用命中 memo
      }
      changed = true;
      return n;
    });
    return changed ? result : prev;
  }
  // 长度不一致：按 taskId 合并，prev 中有但 next 中缺失的乐观项保留
  const nextMap = new Map(next.map((w) => [w.taskId, w]));
  const merged: WorkItem[] = [];
  const seen = new Set<string | number>();
  // 先按 prev 顺序保留乐观项或更新为 next 的最新值
  for (const p of prev) {
    const n = nextMap.get(p.taskId);
    if (n) {
      merged.push(n);
    } else {
      // next 中缺失，保留 prev 乐观项（后端尚未同步）
      merged.push(p);
    }
    seen.add(p.taskId);
  }
  // 追加 next 中新增的项（prev 中没有的）
  for (const n of next) {
    if (!seen.has(n.taskId)) {
      merged.push(n);
    }
  }
  return merged;
};

// ====== 工具函数 ======

/** 将比例字符串（如 "16:9"）转换为 CSS aspect-ratio 值（如 "16 / 9"） */
const ratioToCss = (ratio: string): string => {
  if (ratio === "adaptive" || !ratio.includes(":")) return "16 / 9";
  const [w, h] = ratio.split(":");
  return `${w} / ${h}`;
};

/**
 * 获取视频文件（本地 File 或远程 URL）的时长（毫秒）
 * 通过创建临时 video 元素加载 metadata 获取，失败时返回 0
 */
const getVideoDurationMillis = (src: string): Promise<number> =>
  new Promise((resolve) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    const cleanup = () => {
      video.onloadedmetadata = null;
      video.onerror = null;
    };
    video.onloadedmetadata = () => {
      const ms = Math.round((video.duration || 0) * 1000);
      cleanup();
      resolve(ms);
    };
    video.onerror = () => {
      cleanup();
      resolve(0);
    };
    video.src = src;
  });

// ====== 子组件 ======

/** 下拉选择器（受控） */
interface SelectDropdownProps {
  icon: ReactNode;
  label: string;
  options: SelectOption[];
  open: boolean;
  disabled?: boolean;
  title?: string;
  onTriggerClick: () => void;
  onOptionSelect: (value: string, label: string) => void;
}

const SelectDropdown: FC<SelectDropdownProps> = memo(
  ({
    icon,
    label,
    options,
    open,
    disabled,
    title,
    onTriggerClick,
    onOptionSelect,
  }) => {
    return (
      <div className="select-wrap">
        <button
          type="button"
          className="select-button"
          aria-expanded={open}
          disabled={disabled}
          title={title}
          onClick={(event) => {
            event.stopPropagation();
            if (disabled) return;
            onTriggerClick();
          }}
        >
          {icon}
          <span>{label}</span>
          <ChevronDown />
        </button>
        <div className={`select-menu ${open ? "open" : ""}`}>
          {options
            .filter((option) => !option.hidden)
            .map((option) => (
              <button
                key={option.value}
                type="button"
                className={option.label === label ? "selected" : ""}
                onClick={(event) => {
                  event.stopPropagation();
                  onOptionSelect(option.value, option.label);
                }}
              >
                {option.label}
              </button>
            ))}
        </div>
      </div>
    );
  },
);
SelectDropdown.displayName = "SelectDropdown";

/** 媒体上传槽位 */
interface MediaSlotProps {
  icon: ReactNode;
  title: string;
  hint: string;
  count: number;
  accept: string;
  multiple: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onChange: (files: File[]) => void;
  /** 远程素材预览项（来自"再次编辑"回填的后端 URL） */
  remoteFiles?: RemoteFile[];
}

const MediaSlot: FC<MediaSlotProps> = memo(
  ({
    icon,
    title,
    hint,
    count,
    accept,
    multiple,
    inputRef,
    onChange,
    remoteFiles,
  }) => {
    const hasRemote = remoteFiles && remoteFiles.length > 0;
    const totalCount = count + (hasRemote ? remoteFiles.length : 0);
    return (
      <label className={`media-slot ${totalCount > 0 ? "has-files" : ""}`}>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={(event) => onChange(Array.from(event.target.files ?? []))}
        />
        <span className="media-slot-icon">{icon}</span>
        <span className="media-slot-copy">
          <strong>{title}</strong>
          <small>{hint}</small>
        </span>
        <span className="media-count">{totalCount}</span>
      </label>
    );
  },
);
MediaSlot.displayName = "MediaSlot";

/** 灵感卡片 */
interface IdeaCardViewProps {
  idea: IdeaItem;
  index: number;
  onClick: (idea: IdeaItem) => void;
}

const IdeaCardView: FC<IdeaCardViewProps> = memo(({ idea, index, onClick }) => {
  return (
    <button type="button" className="idea-card" onClick={() => onClick(idea)}>
      <span className="idea-top">
        <span className="idea-index">{String(index + 1).padStart(2, "0")}</span>
        <span className="idea-tag">{idea.duration}</span>
      </span>
      <strong>{idea.title}</strong>
      <p>{idea.summary}</p>
      <span className="idea-arrow" aria-hidden="true">
        <ArrowUpRight />
      </span>
    </button>
  );
});
IdeaCardView.displayName = "IdeaCardView";

/** 视频模板卡片 */
interface TemplateCardViewProps {
  template: TemplateItem;
  /** 点击"做同款"按钮：回显参数与素材到编辑器 */
  onUse: (template: TemplateItem) => void;
  /** 点击播放按钮 / overlay：跳转到模板详情页 */
  onView: (template: TemplateItem) => void;
}

const TemplateCardView: FC<TemplateCardViewProps> = memo(
  ({ template, onUse, onView }) => {
    // adaptive 自适应比例下从视频元数据探测到的真实宽高比（如 "9:16"）
    const [detectedRatio, setDetectedRatio] = useState<string | null>(null);
    // adaptive 时优先用探测结果，避免误判为 16:9 导致竖屏视频展示不全
    const effectiveRatio =
      template.ratio === "adaptive" && detectedRatio
        ? detectedRatio
        : template.ratio;
    const durationLabel =
      template.duration > 0
        ? `00:${String(template.duration).padStart(2, "0")}`
        : "";
    return (
      <article
        className="video-card"
        data-kind="template"
        data-template-id={template.templateId}
        data-category={template.category}
      >
        <div
          className="video-preview"
          style={{ aspectRatio: ratioToCss(effectiveRatio) }}
        >
          {/* 兜底封面层：始终渲染，视频加载前/返回列表后用渐变占位避免黑屏 */}
          <div
            className="preview-art"
            style={{ ["--poster" as string]: template.poster }}
          />
          {template.videoUrl ? (
            <WorkVideoThumb
              videoUrl={template.videoUrl}
              onRatioDetected={setDetectedRatio}
            />
          ) : null}
          <span
            className="play-button"
            role="button"
            tabIndex={0}
            onClick={(event) => {
              event.stopPropagation();
              onView(template);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onView(template);
              }
            }}
          >
            <Play />
          </span>
          {durationLabel && (
            <span className="duration-badge">{durationLabel}</span>
          )}
          <div
            className="video-template-overlay"
            onClick={(event) => {
              event.stopPropagation();
              onView(template);
            }}
          >
            <div className="template-overlay-top">
              <span className="template-overlay-label">

              </span>
            </div>
            <button
              type="button"
              className="template-use"
              onClick={(event) => {
                event.stopPropagation();
                onUse(template);
              }}
            >
              <Sparkles />
              做同款
            </button>
          </div>
        </div>
        <div className="card-body">
          <h3 className="card-title">{template.title}</h3>
          <div className="card-meta">
            {template.meta.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
        </div>
      </article>
    );
  },
);
TemplateCardView.displayName = "TemplateCardView";

/**
 * 作品视频首帧懒加载预览
 * 优化卡顿：初始不加载视频，进入视口才加载 metadata + 首帧
 * 离开视口后释放 src，避免多视频同时占用带宽/内存
 */
interface WorkVideoThumbProps {
  videoUrl: string;
  /** 视频元数据加载完成时回传真实宽高比（如 "9:16"），供外层 adaptive 容器修正布局 */
  onRatioDetected?: (ratio: string) => void;
}

const WorkVideoThumb: FC<WorkVideoThumbProps> = memo(({ videoUrl, onRatioDetected }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // 是否已进入视口（控制 src 是否设置）
  const [shouldLoad, setShouldLoad] = useState(false);
  // 首帧是否已渲染（控制透明度过渡，避免黑屏闪烁）
  const [frameReady, setFrameReady] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    // IntersectionObserver 监测视频是否进入视口
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // 进入视口：开始加载
            setShouldLoad(true);
          } else {
            // 离开视口：暂停并释放 src，下次进入时重新加载
            try {
              v.pause();
              v.removeAttribute("src");
              v.load();
            } catch {
              /* 忽略释放异常 */
            }
            setShouldLoad(false);
            setFrameReady(false);
          }
        });
      },
      // 提前 200px 触发，避免滚动到时才加载出现空白
      { rootMargin: "200px 0px" },
    );
    observer.observe(v);
    return () => observer.disconnect();
  }, []);

  // shouldLoad 变化时同步设置 src
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (shouldLoad && !v.getAttribute("src")) {
      v.setAttribute("src", `${videoUrl}#t=0.1`);
      v.load();
    }
  }, [shouldLoad, videoUrl]);

  return (
    <video
      ref={videoRef}
      className={`preview-video${frameReady ? " is-loaded" : ""}`}
      muted
      loop
      playsInline
      // 仅加载元数据（首帧附近），不预加载整段视频
      preload="metadata"
      // 元数据加载后手动 seek 到 0.1 秒，确保首帧画面渲染
      onLoadedMetadata={(e) => {
        const v = e.target as HTMLVideoElement;
        try {
          v.currentTime = 0.1;
        } catch {
          /* 忽略 seek 异常 */
        }
        // 探测真实宽高比，回传给外层 adaptive 容器修正布局
        const w = v.videoWidth;
        const h = v.videoHeight;
        if (w > 0 && h > 0) {
          onRatioDetected?.(`${w}:${h}`);
        }
      }}
      onLoadedData={() => setFrameReady(true)}
      onMouseEnter={(e) => {
        const v = e.target as HTMLVideoElement;
        // 仅在首帧已加载时才播放，避免加载中触发 play 报错
        if (v.readyState >= 2) v.play().catch(() => {});
      }}
      onMouseLeave={(e) => {
        const v = e.target as HTMLVideoElement;
        v.pause();
        // 重置回首帧，避免下次悬停前停留在最后播放位置导致黑屏
        try {
          v.currentTime = 0.1;
        } catch {
          /* 忽略 seek 异常 */
        }
      }}
    />
  );
});
WorkVideoThumb.displayName = "WorkVideoThumb";

/** 已生成视频作品卡片（参考 ai生图 overlay 设计） */
interface WorkCardViewProps {
  work: WorkItem;
  onView: (work: WorkItem) => void;
  onDelete: (work: WorkItem) => void;
  onReuse: (work: WorkItem) => void;
  onDownload: (work: WorkItem) => void;
}

const WorkCardView: FC<WorkCardViewProps> = memo(
  ({ work, onView, onDelete, onReuse, onDownload }) => {
    /** 三点菜单展开状态 */
    const [menuOpen, setMenuOpen] = useState(false);
    // adaptive 自适应比例下从视频元数据探测到的真实宽高比（如 "9:16"）
    const [detectedRatio, setDetectedRatio] = useState<string | null>(null);
    const ratioLabel = work.ratio === "adaptive" ? "自适应" : work.ratio;
    // adaptive 时优先用探测结果，避免误判为 16:9 导致竖屏视频展示不全
    const effectiveRatio =
      work.ratio === "adaptive" && detectedRatio ? detectedRatio : work.ratio;
    const durationLabel =
      work.duration > 0 ? `00:${String(work.duration).padStart(2, "0")}` : "";
    // 失败状态：status === "failed"，展示失败原因
    const failed = isFailedStatus(work.status);
    // 失败原因：优先用后端返回的 errorMessage，兜底文案
    const failReason = work.errorMessage?.trim() || "视频生成失败";

    return (
      <article
        className={`video-card work-card${work.pending ? " pending" : ""}${failed ? " failed" : ""}`}
        data-kind="work"
        data-work-id={work.id}
      >
        <div
          className="video-preview"
          style={{ aspectRatio: ratioToCss(effectiveRatio) }}
        >
          {/* 兜底封面层：始终渲染，视频加载前/失败/返回列表后用渐变占位避免黑屏 */}
          <div
            className="preview-art"
            style={{ ["--poster" as string]: work.poster }}
          />
          {work.videoUrl && !work.pending && !failed ? (
            <WorkVideoThumb
              videoUrl={work.videoUrl}
              onRatioDetected={setDetectedRatio}
            />
          ) : null}
          {/* 视频封面中央播放按钮：与详情页 detail-play 一致，点击进入详情 */}
          {work.videoUrl && !work.pending && !failed && (
            <button
              type="button"
              className="work-play-btn"
              aria-label="查看作品"
              onClick={(e) => {
                e.stopPropagation();
                onView(work);
              }}
            >
              <Play size={21} />
            </button>
          )}
          {work.pending ? (
            <div className="pending-state">
              <span>
                <Loader2 />
                正在生成
              </span>
            </div>
          ) : failed ? (
            // 失败状态：展示失败图标与原因，不显示播放按钮
            <div className="failed-state">
              <AlertCircle />
              <span className="failed-title">生成失败</span>
              <span className="failed-reason">{failReason}</span>
            </div>
          ) : !work.videoUrl ? (
            <span className="play-button">
              <Play />
            </span>
          ) : null}
          {durationLabel && !failed && (
            <span className="duration-badge">{durationLabel}</span>
          )}
          {failed && <span className="status-badge failed">失败</span>}

          {/* hover 遮罩层：顶部删除 + 底部操作按钮（失败时也保留，便于删除/重试） */}
          {!work.pending && (
            <div className="work-overlay">
              <div className="overlay-topline">
                <div className="overlay-topline-actions">
                  <div className="work-more-wrap">
                    <button
                      type="button"
                      className="overlay-icon"
                      title="更多"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpen((v) => !v);
                      }}
                    >
                      <MoreHorizontal size={12} />
                    </button>
                    {menuOpen && (
                      <div
                        className="work-more-popover"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Popconfirm
                          title="确定要删除这个作品吗？"
                          okText="确定"
                          cancelText="取消"
                          placement="top"
                          onPopupClick={(e) => e.stopPropagation()}
                          onConfirm={() => {
                            setMenuOpen(false);
                            onDelete(work);
                          }}
                        >
                          <button
                            type="button"
                            className="work-more-item danger"
                          >
                            <Trash2 size={13} />
                            删除作品
                          </button>
                        </Popconfirm>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="work-overlay-actions">
                {/* 失败状态无视频可查看，仅保留"再次编辑"用于重新生成 */}
                {work.videoUrl && !failed && (
                  <button
                    type="button"
                    className="overlay-btn primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      onView(work);
                    }}
                  >
                    <Eye size={13} />
                    查看作品
                  </button>
                )}
                <button
                  type="button"
                  className="overlay-btn secondary"
                  style={failed ? { gridColumn: "1 / -1" } : undefined}
                  onClick={(e) => {
                    e.stopPropagation();
                    onReuse(work);
                  }}
                >
                  <Sparkles size={13} />
                  {failed ? "重新生成" : "再次编辑"}
                </button>
                {work.videoUrl && !failed && (
                  <button
                    type="button"
                    className="overlay-btn secondary icon-only"
                    title="下载"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDownload(work);
                    }}
                  >
                    <Download size={13} />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="card-body">
          <h3 className="card-title">
            {work.pending
              ? `${work.modeLabel}处理中…`
              : failed
                ? `${work.modeLabel}生成失败`
                : work.prompt.slice(0, 24) || `新生成的${work.modeLabel}`}
          </h3>
          {/* <p className="card-prompt">{failed ? failReason : work.prompt}</p> */}
          <div className="card-meta">
            {work.resolution && <span>{work.resolution}</span>}
            <span>{ratioLabel}</span>
          </div>
        </div>
      </article>
    );
  },
);
WorkCardView.displayName = "WorkCardView";

// ====== 主组件 ======

export const AiVideo: FC = () => {
  const navigate = useNavigate();
  // 生成模式与参数选择器状态
  const [mode, setMode] = useState<Mode>("text");
  const [model] = useState<string>("MiniMax-H3");
  const [resolution, setResolution] = useState<string>("768P");
  const [duration, setDuration] = useState<number>(4);
  const [ratio, setRatio] = useState<string>("9:16");
  const [ratioLocked, setRatioLocked] = useState<boolean>(false);
  const [aigcWatermark, setAigcWatermark] = useState<boolean>(false);

  // 提示词
  const [prompt, setPrompt] = useState<string>(DEFAULT_PROMPT);

  // 上传文件（本地 File 对象，用户主动上传）
  const [files, setFiles] = useState<FilesState>({
    first: [],
    last: [],
    referenceImage: [],
    referenceVideo: [],
    referenceAudio: [],
  });

  // 远程素材（来自"再次编辑"回填的后端 URL，提交时通过 fetch 转 File）
  const [remoteFiles, setRemoteFiles] = useState<RemoteFilesState>({
    first: [],
    last: [],
    referenceImage: [],
    referenceVideo: [],
    referenceAudio: [],
  });

  // 画廊与筛选（从 sessionStorage 恢复上次所在 tab，刷新或从详情返回后保持位置）
  const [activeGallery, setActiveGallery] = useState<GalleryTab>(() => {
    try {
      const saved = sessionStorage.getItem("ai_video_active_gallery");
      if (saved === "templates" || saved === "works") return saved;
    } catch {
      /* sessionStorage 不可用时忽略 */
    }
    return "templates";
  });
  const [activeTemplateFilter, setActiveTemplateFilter] =
    useState<TemplateFilter>("all");

  // 模板列表（来自接口）
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState<boolean>(false);

  // 作品列表
  const [works, setWorks] = useState<WorkItem[]>([]);
  const [worksLoading, setWorksLoading] = useState<boolean>(false);

  // Toast 与生成状态
  const [toast, setToast] = useState<{ visible: boolean; text: string }>({
    visible: false,
    text: "",
  });
  const [generating, setGenerating] = useState<boolean>(false);

  // 积分预估（3 秒防抖调用 POST /ai-video/point-preview）
  const [estimatedPoint, setEstimatedPoint] = useState<number | null>(null);
  const [pointLoading, setPointLoading] = useState<boolean>(false);
  // 积分预估触发器：上传参考素材时自增，触发 effect 重新预估
  const [pointDirty, setPointDirty] = useState<number>(0);

  // 下拉菜单打开状态
  const [openMenu, setOpenMenu] = useState<SelectKey | null>(null);

  // DOM 引用
  const composerRef = useRef<HTMLDivElement | null>(null);
  const promptRef = useRef<HTMLTextAreaElement | null>(null);
  const worksSectionRef = useRef<HTMLElement | null>(null);
  // 视频模板网格容器（用于 masonry 列高度对齐）
  const videoGridRef = useRef<HTMLDivElement | null>(null);
  const firstFrameInputRef = useRef<HTMLInputElement | null>(null);
  const lastFrameInputRef = useRef<HTMLInputElement | null>(null);
  const referenceImageInputRef = useRef<HTMLInputElement | null>(null);
  const referenceVideoInputRef = useRef<HTMLInputElement | null>(null);
  const referenceAudioInputRef = useRef<HTMLInputElement | null>(null);

  // 定时器引用（组件卸载时清理）
  const toastTimerRef = useRef<number | null>(null);
  const generateTimerRef = useRef<number | null>(null);
  // 轮询定时器 ID（setInterval）
  const pollIntervalRef = useRef<number | null>(null);
  // 是否有进行中的任务（useRef 避免 useEffect 频繁重置定时器）
  const hasPendingRef = useRef<boolean>(false);
  // 积分预估防抖定时器
  const pointDebounceRef = useRef<number | null>(null);
  // 编辑器聚焦定时器（回填参数后延迟聚焦文本框，组件卸载时清理避免空指针）
  const focusTimerRef = useRef<number | null>(null);

  /** 是否为帧生成模式（首帧 / 尾帧 / 首尾帧） */
  const isFrameMode =
    mode === "first" || mode === "last" || mode === "first_last";

  /** 显示 Toast 提示消息（2.4s 后自动消失） */
  const notify = useCallback((message: string) => {
    setToast({ visible: true, text: message });
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(
      () => setToast({ visible: false, text: "" }),
      2400,
    );
  }, []);

  /** 加载模板列表 GET /ai-video/templates，接口返回空时用本地 TEMPLATE_CARDS 兜底 */
  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const res = await getAiVideoTemplateListApi();
      if (res.code === 0) {
        const list = extractList(res);
        if (list.length > 0) {
          setTemplates(list.map(mapTemplateToItem));
        } else {
          // 接口返回空：使用本地写死的 12 个模板兜底（对齐 28.AI视频.html）
          setTemplates(TEMPLATE_CARDS);
        }
      } else {
        notify(res.msg || "模板加载失败");
      }
    } catch (e: any) {
      notify(e?.message || "模板加载失败");
    } finally {
      setTemplatesLoading(false);
    }
  }, [notify]);

  /**
   * 全局轮询：30 秒一次调用 GET /ai-video/tasks 列表接口
   * 更新所有作品状态，全部完成后自动停止，刷新页面后仍可恢复轮询
   */
  const startPolling = useCallback(() => {
    // 已有定时器在运行则跳过，避免重复启动
    if (pollIntervalRef.current) return;
    pollIntervalRef.current = window.setInterval(async () => {
      try {
        const res = await getAiVideoTaskListApi();
        if (res.code === 0) {
          const list = extractList(res);
          // 更新作品状态（按 taskId 做 diff，未变项保留原引用，避免全量卡片重渲染）
          setWorks((prev) => mergeWorks(prev, list.map(mapTaskToWork)));
          // 更新是否有进行中任务
          const stillPending = list.some((t) => isPendingStatus(t.status));
          hasPendingRef.current = stillPending;
          // 全部完成则自动停止轮询
          if (!stillPending && pollIntervalRef.current) {
            window.clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        }
      } catch {
        // 网络异常静默忽略，30 秒后自动重试
      }
    }, 30000);
  }, []);

  /** 停止轮询：清除 setInterval 定时器 */
  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      window.clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  /** 加载我的作品列表 GET /ai-video/tasks，若有进行中任务则启动 30 秒轮询 */
  const loadWorks = useCallback(async () => {
    setWorksLoading(true);
    try {
      const res = await getAiVideoTaskListApi();
      if (res.code === 0) {
        const list = extractList(res);
        setWorks((prev) => mergeWorks(prev, list.map(mapTaskToWork)));
        // 记录是否有进行中任务（刷新页面后据此恢复轮询）
        const pending = list.some((t) => isPendingStatus(t.status));
        hasPendingRef.current = pending;
        // 有进行中任务则启动轮询（startPolling 内部会避免重复启动）
        if (pending) startPolling();
      } else {
        notify(res.msg || "作品加载失败");
      }
    } catch (e: any) {
      notify(e?.message || "作品加载失败");
    } finally {
      setWorksLoading(false);
    }
  }, [notify, startPolling]);

  /** 切换生成模式，并联动比例选择器（帧模式锁定自适应），清空所有已上传素材 */
  const applyMode = useCallback((nextMode: Mode) => {
    setMode(nextMode);
    const frameMode =
      nextMode === "first" || nextMode === "last" || nextMode === "first_last";
    if (frameMode) {
      setRatio("adaptive");
      setRatioLocked(true);
    } else if (nextMode === "reference") {
      setRatio("adaptive");
      setRatioLocked(false);
    } else {
      setRatioLocked(false);
      setRatio((prev) => (prev === "adaptive" ? "16:9" : prev));
    }
    // 切换模式时清空所有上传素材（本地 + 远程），避免不同模式间素材混淆
    setFiles({
      first: [],
      last: [],
      referenceImage: [],
      referenceVideo: [],
      referenceAudio: [],
    });
    setRemoteFiles({
      first: [],
      last: [],
      referenceImage: [],
      referenceVideo: [],
      referenceAudio: [],
    });
    // 清空文件输入框的 value，否则同名文件无法再次选择
    if (firstFrameInputRef.current) firstFrameInputRef.current.value = "";
    if (lastFrameInputRef.current) lastFrameInputRef.current.value = "";
    if (referenceImageInputRef.current)
      referenceImageInputRef.current.value = "";
    if (referenceVideoInputRef.current)
      referenceVideoInputRef.current.value = "";
    if (referenceAudioInputRef.current)
      referenceAudioInputRef.current.value = "";
    // 触发积分预估刷新
    setPointDirty((v) => v + 1);
    setOpenMenu(null);
  }, []);

  /** 切换下拉菜单展开状态 */
  const handleToggleMenu = useCallback((key: SelectKey) => {
    setOpenMenu((prev) => (prev === key ? null : key));
  }, []);

  /** 选择器选项被选中后更新对应状态 */
  const handleSelectOption = useCallback((key: SelectKey, value: string) => {
    if (key === "resolution") setResolution(value);
    else if (key === "duration") setDuration(Number(value));
    else if (key === "ratio") {
      setRatio(value);
      setRatioLocked(false);
    }
    // model 为固定值，无需更新
    setOpenMenu(null);
  }, []);

  /** 处理文件上传：校验数量与大小，更新对应文件列表，并清空该槽位的远程素材 */
  const handleFileChange = useCallback(
    async (
      fileKey: FileKey,
      selected: File[],
      maxCount: number,
      maxSizeMb: number,
      defaultHint: string,
    ) => {
      if (selected.length > maxCount) {
        notify(`最多可选择 ${maxCount} 个文件`);
        return;
      }
      const oversized = selected.find(
        (file) => file.size > maxSizeMb * 1024 * 1024,
      );
      if (oversized) {
        notify(`${oversized.name} 超过 ${maxSizeMb}MB 限制`);
        return;
      }
      // 多模态参考素材三槽位总和限制：参考图片 + 参考视频 + 参考音频 ≤ 12
      // 上传到当前槽位时，远程素材会被清空，新总和 = selected + 其它两槽位(本地+远程)
      if (
        fileKey === "referenceImage" ||
        fileKey === "referenceVideo" ||
        fileKey === "referenceAudio"
      ) {
        const otherKeys = (
          ["referenceImage", "referenceVideo", "referenceAudio"] as FileKey[]
        ).filter((k) => k !== fileKey);
        const otherLocal = otherKeys.reduce(
          (sum, k) => sum + files[k].length,
          0,
        );
        const otherRemote = otherKeys.reduce(
          (sum, k) => sum + remoteFiles[k].length,
          0,
        );
        const newTotal = selected.length + otherLocal + otherRemote;
        if (newTotal > 12) {
          notify("最多上传 12 个文件进行创作");
          return;
        }
      }
      // 参考视频总时长限制：单批上传视频总时长 ≤ 15 秒，超出阻止上传
      if (fileKey === "referenceVideo" && selected.length > 0) {
        const objectUrls = selected.map((file) => URL.createObjectURL(file));
        try {
          const durations = await Promise.all(
            objectUrls.map((url) => getVideoDurationMillis(url)),
          );
          const totalSec = durations.reduce((sum, ms) => sum + ms, 0) / 1000;
          if (totalSec > 15) {
            notify("参考视频总时长不能超过 15 秒");
            return;
          }
        } finally {
          objectUrls.forEach((url) => URL.revokeObjectURL(url));
        }
      }
      setFiles((prev) => ({ ...prev, [fileKey]: selected }));
      // 用户重新上传时清空该槽位的远程素材，避免重复提交
      setRemoteFiles((prev) => ({ ...prev, [fileKey]: [] }));
      // 多模态参考素材变化时触发积分预估刷新
      if (
        fileKey === "referenceImage" ||
        fileKey === "referenceVideo" ||
        fileKey === "referenceAudio"
      ) {
        setPointDirty((v) => v + 1);
      }
    },
    [notify, files, remoteFiles],
  );

  /** 计算单个上传槽位的提示文案（文件名 / 已选数量 / 默认提示，同时考虑远程素材） */
  const buildSlotHint = useCallback(
    (fileKey: FileKey, defaultHint: string): string => {
      const list = files[fileKey];
      const remotes = remoteFiles[fileKey];
      const total = list.length + remotes.length;
      if (total === 0) return defaultHint;
      // 本地单文件优先显示文件名
      if (list.length === 1 && remotes.length === 0) return list[0].name;
      // 远程单文件优先显示 originalName
      if (remotes.length === 1 && list.length === 0)
        return remotes[0].originalName || "已回填 1 个素材";
      return `已选择 ${total} 个文件`;
    },
    [files, remoteFiles],
  );

  /** 点击灵感卡片，填入提示词并滚动到编辑器 */
  const handleIdeaClick = useCallback(
    (idea: IdeaItem) => {
      setPrompt(idea.prompt);
      composerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      if (focusTimerRef.current) window.clearTimeout(focusTimerRef.current);
      focusTimerRef.current = window.setTimeout(
        () => promptRef.current?.focus(),
        320,
      );
      notify("灵感提示词已填入，可继续修改");
    },
    [notify],
  );

  /**
   * 点击"做同款"：直接回显模板参数与输入素材到编辑器（文字/图片/视频）
   * 切换到对应生成模式 tab，回填提示词、分辨率、时长、比例
   * 有图片回填图片，有视频回填视频（categorizeInputFiles 按 usage/category 分类）
   */
  const handleUseTemplate = useCallback(
    (template: TemplateItem) => {
      const raw = template.raw;
      if (!raw) return;
      // 后端原始 generationMode 映射到前端 Mode（如 FIRST_FRAME → first）
      const modeKey = raw.generationMode
        ? API_TO_MODE[raw.generationMode] || "text"
        : raw.referenceType
          ? REF_TYPE_TO_MODE[raw.referenceType] || "text"
          : "text";
      // 切换到对应生成模式（applyMode 内部会清空素材，后续 setRemoteFiles 回填）
      applyMode(modeKey);
      // 回显提示词
      setPrompt(template.prompt || DEFAULT_PROMPT);
      // 回显分辨率 / 时长 / 比例
      if (raw.resolution) setResolution(raw.resolution);
      if (raw.duration && raw.duration > 0) setDuration(raw.duration);
      if (raw.aspectRatio) {
        setRatio(raw.aspectRatio);
        setRatioLocked(false);
      }
      // 清空本地文件，回填后端返回的输入素材 URL 到远程素材状态
      setFiles({
        first: [],
        last: [],
        referenceImage: [],
        referenceVideo: [],
        referenceAudio: [],
      });
      setRemoteFiles(categorizeInputFiles(raw.inputFiles));
      setOpenMenu(null);
      // 触发积分预估刷新
      setPointDirty((v) => v + 1);
      // 滚动到编辑器并聚焦文本输入框
      composerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      if (focusTimerRef.current) window.clearTimeout(focusTimerRef.current);
      focusTimerRef.current = window.setTimeout(
        () => promptRef.current?.focus(),
        350,
      );
      notify(
        raw.inputFiles && raw.inputFiles.length > 0
          ? `已载入「${template.title}」参数与 ${raw.inputFiles.length} 个素材`
          : `已载入「${template.title}」模板参数，可继续修改`,
      );
    },
    [applyMode, notify],
  );

  /**
   * 点击播放按钮 / overlay：跳转到模板详情页
   * 通过 location.state 携带模板数据，详情页优先用 state 立即渲染
   */
  const handleViewTemplate = useCallback(
    (template: TemplateItem) => {
      navigate(`/hub/ai-video/detail?template=${template.templateId}`, {
        state: { template },
      });
    },
    [navigate],
  );

  /** 点击“查看作品”，跳转到作品详情页 */
  const handleViewWork = useCallback(
    (work: WorkItem) => {
      navigate(`/hub/ai-video/detail?work=${work.taskId}`);
    },
    [navigate],
  );

  /** 点击“再次编辑/重新生成”，将作品参数与输入素材回显到编辑器 */
  const handleReuseWork = useCallback(
    (work: WorkItem) => {
      // 切换到对应生成模式（同时联动比例锁定逻辑）
      applyMode(work.mode);
      // 回显提示词到文本输入框
      setPrompt(work.prompt || DEFAULT_PROMPT);
      // 回显分辨率 / 时长 / 比例
      if (work.resolution) setResolution(work.resolution);
      if (work.duration > 0) setDuration(work.duration);
      if (work.ratio) {
        setRatio(work.ratio);
        setRatioLocked(false);
      }
      // 清空本地文件，回填后端返回的输入素材 URL 到远程素材状态
      setFiles({
        first: [],
        last: [],
        referenceImage: [],
        referenceVideo: [],
        referenceAudio: [],
      });
      setRemoteFiles(categorizeInputFiles(work.inputFiles));
      setOpenMenu(null);
      // 滚动到编辑器并聚焦文本输入框
      composerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      if (focusTimerRef.current) window.clearTimeout(focusTimerRef.current);
      focusTimerRef.current = window.setTimeout(
        () => promptRef.current?.focus(),
        350,
      );
      notify(
        work.inputFiles && work.inputFiles.length > 0
          ? `已载入「${work.modeLabel}」参数与 ${work.inputFiles.length} 个素材`
          : `已载入「${work.modeLabel}」作品参数，可继续修改`,
      );
    },
    [applyMode, notify],
  );

  /** 删除作品 DELETE /ai-video/tasks/{id} */
  const handleDeleteWork = useCallback(
    async (work: WorkItem) => {
      try {
        const res = await deleteAiVideoTaskApi(work.taskId);
        if (res.code === 0) {
          setWorks((prev) => prev.filter((w) => w.taskId !== work.taskId));
          notify("作品已删除");
        } else {
          notify(res.msg || "删除失败");
        }
      } catch (e: any) {
        notify(e?.message || "删除失败");
      }
    },
    [notify],
  );

  /** 下载作品视频 */
  const handleDownloadWork = useCallback((work: WorkItem) => {
    if (!work.videoUrl) return;
    // 从 URL 提取扩展名，兜底 mp4
    const ext = work.videoUrl.split("?")[0].split(".").pop() || "mp4";
    downloadFileSmart(
      work.videoUrl,
      `ai-video-work-${work.taskId}-${Date.now()}.${ext}`,
    );
  }, []);

  /** 校验生成请求参数，返回错误消息（空字符串表示通过） */
  const validateRequest = useCallback((): string => {
    if (!prompt.trim()) return "请先输入视频描述";
    // 同时考虑本地文件和远程回填素材
    const hasFirst = files.first.length > 0 || remoteFiles.first.length > 0;
    const hasLast = files.last.length > 0 || remoteFiles.last.length > 0;
    if (mode === "first" && !hasFirst) return "请上传首帧图片";
    if (mode === "last" && !hasLast) return "请上传尾帧图片";
    if (mode === "first_last" && (!hasFirst || !hasLast))
      return "请同时上传首帧和尾帧图片";
    if (mode === "reference") {
      const hasImages =
        files.referenceImage.length > 0 ||
        remoteFiles.referenceImage.length > 0;
      const hasVideos =
        files.referenceVideo.length > 0 ||
        remoteFiles.referenceVideo.length > 0;
      const hasAudios =
        files.referenceAudio.length > 0 ||
        remoteFiles.referenceAudio.length > 0;
      if (!hasImages && !hasVideos && !hasAudios)
        return "请至少上传一种参考素材";
      if (hasAudios && !hasImages && !hasVideos)
        return "参考音频不能单独使用，请同时添加参考图片或视频";
    }
    return "";
  }, [prompt, mode, files, remoteFiles]);

  /** 点击生成按钮：校验 → 乐观插入"正在生成"卡片 → 调用创建任务接口 → 轮询任务状态 */
  const handleGenerate = useCallback(async () => {
    const error = validateRequest();
    if (error) {
      if (!prompt.trim()) promptRef.current?.focus();
      notify(error);
      return;
    }
    const trimmedPrompt = prompt.trim();
    const modeLabel = MODE_LABELS[mode];

    // 乐观更新：立即在列表顶部插入一张"正在生成"卡片，无需等待接口返回
    const tempId = `temp-${Date.now()}`;
    const optimisticWork: WorkItem = {
      id: tempId,
      taskId: 0, // 临时占位，接口成功后替换为真实 taskId
      mode,
      modeLabel,
      prompt: trimmedPrompt,
      model: "MiniMax-H3",
      resolution,
      duration,
      ratio,
      poster: "",
      videoUrl: "",
      pending: true,
      createdAt: new Date().toISOString(),
    };
    setWorks((prev) => [optimisticWork, ...prev]);
    setActiveGallery("works");
    try {
      sessionStorage.setItem("ai_video_active_gallery", "works");
    } catch {
      /* sessionStorage 不可用时忽略 */
    }
    worksSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    notify(`${modeLabel}任务已提交，正在等待生成结果`);
    setGenerating(true);

    try {
      // 提交前把远程回填素材通过 fetch 转 File，合并本地文件后提交
      // 避免"再次编辑"回填的 URL 素材丢失
      const remoteFirstFiles = await Promise.all(
        remoteFiles.first.map((rf) => fetchUrlToFile(rf.url, rf.originalName)),
      );
      const remoteLastFiles = await Promise.all(
        remoteFiles.last.map((rf) => fetchUrlToFile(rf.url, rf.originalName)),
      );
      const remoteRefImageFiles = await Promise.all(
        remoteFiles.referenceImage.map((rf) =>
          fetchUrlToFile(rf.url, rf.originalName),
        ),
      );
      const remoteRefVideoFiles = await Promise.all(
        remoteFiles.referenceVideo.map((rf) =>
          fetchUrlToFile(rf.url, rf.originalName),
        ),
      );
      const remoteRefAudioFiles = await Promise.all(
        remoteFiles.referenceAudio.map((rf) =>
          fetchUrlToFile(rf.url, rf.originalName),
        ),
      );
      // 合并本地 File 与远程转 File，过滤转换失败的 null
      const mergedFirst = [
        ...files.first,
        ...remoteFirstFiles.filter((f): f is File => f !== null),
      ];
      const mergedLast = [
        ...files.last,
        ...remoteLastFiles.filter((f): f is File => f !== null),
      ];
      const mergedRefImages = [
        ...files.referenceImage,
        ...remoteRefImageFiles.filter((f): f is File => f !== null),
      ];
      const mergedRefVideos = [
        ...files.referenceVideo,
        ...remoteRefVideoFiles.filter((f): f is File => f !== null),
      ];
      const mergedRefAudios = [
        ...files.referenceAudio,
        ...remoteRefAudioFiles.filter((f): f is File => f !== null),
      ];

      const res = await createAiVideoTaskApi({
        prompt: trimmedPrompt,
        generationMode: MODE_TO_API[mode],
        enhancePrompt: aigcWatermark,
        resolution,
        duration,
        aspectRatio: ratio,
        firstFrame:
          mode === "first" || mode === "first_last"
            ? mergedFirst[0]
            : undefined,
        lastFrame:
          mode === "last" || mode === "first_last" ? mergedLast[0] : undefined,
        referenceImages: mode === "reference" ? mergedRefImages : undefined,
        referenceVideos: mode === "reference" ? mergedRefVideos : undefined,
        referenceAudios: mode === "reference" ? mergedRefAudios : undefined,
      });
      if (res.code === 0 && res.data && res.data.taskId) {
        // 接口成功：创建接口仅返回 taskId + status（如 "queued"），
        // 用提交参数 + taskId 构造作品卡片替换临时乐观项
        const realWork: WorkItem = {
          ...optimisticWork,
          id: `task-${res.data.taskId}`,
          taskId: res.data.taskId,
          status: res.data.status,
          pending: isPendingStatus(res.data.status),
        };
        setWorks((prev) => prev.map((w) => (w.id === tempId ? realWork : w)));
        // 接口成功后清空文本输入与上传素材，准备下一次生成
        setPrompt("");
        setFiles({
          first: [],
          last: [],
          referenceImage: [],
          referenceVideo: [],
          referenceAudio: [],
        });
        setRemoteFiles({
          first: [],
          last: [],
          referenceImage: [],
          referenceVideo: [],
          referenceAudio: [],
        });
        // 清空文件输入框的 value，允许重复选择同一文件
        if (firstFrameInputRef.current) firstFrameInputRef.current.value = "";
        if (lastFrameInputRef.current) lastFrameInputRef.current.value = "";
        if (referenceImageInputRef.current)
          referenceImageInputRef.current.value = "";
        if (referenceVideoInputRef.current)
          referenceVideoInputRef.current.value = "";
        if (referenceAudioInputRef.current)
          referenceAudioInputRef.current.value = "";
        // 创建任务后标记有进行中任务并启动 30 秒全局轮询
        hasPendingRef.current = true;
        startPolling();
        // 立即调用一次列表接口同步后端最新数据，避免乐观项与后端数据不同步
        // 导致连续提交时出现"两个变一个"的闪烁（mergeWorks 会保留乐观项）
        void (async () => {
          try {
            const fresh = await getAiVideoTaskListApi();
            if (fresh.code === 0) {
              const list = extractList(fresh);
              setWorks((prev) => mergeWorks(prev, list.map(mapTaskToWork)));
              hasPendingRef.current = list.some((t) =>
                isPendingStatus(t.status),
              );
            }
          } catch {
            // 静默忽略，30 秒轮询会自动重试
          }
        })();
      } else {
        // 接口返回失败：移除乐观项
        setWorks((prev) => prev.filter((w) => w.id !== tempId));
        notify(res.msg || "任务创建失败");
      }
    } catch (e: any) {
      // 网络异常：移除乐观项
      setWorks((prev) => prev.filter((w) => w.id !== tempId));
      notify(e?.message || "任务创建失败");
    } finally {
      setGenerating(false);
    }
  }, [
    validateRequest,
    prompt,
    mode,
    resolution,
    duration,
    ratio,
    aigcWatermark,
    files,
    remoteFiles,
    notify,
    startPolling,
  ]);

  // 关闭下拉菜单（点击页面任意位置）
  useEffect(() => {
    const closeMenus = () => setOpenMenu(null);
    document.addEventListener("click", closeMenus);
    return () => document.removeEventListener("click", closeMenus);
  }, []);

  /**
   * 积分预估防抖：仅在切换分辨率或时长时触发
   * 500ms 后调用 POST /ai-video/point-preview 查询预计消耗积分
   */
  useEffect(() => {
    // 清除上一次防抖定时器
    if (pointDebounceRef.current) {
      window.clearTimeout(pointDebounceRef.current);
    }
    pointDebounceRef.current = window.setTimeout(async () => {
      // 计算输入图片总数（首帧 + 尾帧 + 参考图片，本地 + 远程）
      const inputImageCount =
        files.first.length +
        files.last.length +
        files.referenceImage.length +
        remoteFiles.first.length +
        remoteFiles.last.length +
        remoteFiles.referenceImage.length;
      // 参考视频：本地 File 直接收集，远程 URL 通过 fetch 转 File 后一起传给后端
      const referenceVideos: File[] = [...files.referenceVideo];
      if (remoteFiles.referenceVideo.length > 0) {
        const remoteFilePromises = remoteFiles.referenceVideo.map(async (r) => {
          const cleaned = (r.url || "").replace(/^['"`]|['"`]$/g, "");
          const url = cleaned.startsWith("http")
            ? cleaned
            : `${VIDEO_CDN_PREFIX}${cleaned.startsWith("/") ? "" : "/"}${cleaned}`;
          const resp = await fetch(url);
          const blob = await resp.blob();
          const name =
            r.originalName || cleaned.split("/").pop() || "reference.mp4";
          return new File([blob], name, { type: blob.type || "video/mp4" });
        });
        const remoteFilesConverted = await Promise.all(remoteFilePromises);
        referenceVideos.push(...remoteFilesConverted);
      }

      setPointLoading(true);
      try {
        const res = await previewAiVideoPointApi({
          resolution,
          duration,
          referenceVideos:
            referenceVideos.length > 0 ? referenceVideos : undefined,
          inputImageCount,
        });
        if (res.code === 0 && res.data) {
          setEstimatedPoint(res.data.estimatedPoint ?? null);
        }
      } catch {
        /* 预估失败静默忽略 */
      } finally {
        setPointLoading(false);
      }
    }, 500);

    return () => {
      if (pointDebounceRef.current) {
        window.clearTimeout(pointDebounceRef.current);
      }
    };
    // 分辨率、时长变化或上传参考素材时触发，文件状态通过闭包读取最新值
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolution, duration, pointDirty]);

  // 初始加载模板列表和作品列表
  useEffect(() => {
    void loadTemplates();
    void loadWorks();
  }, [loadTemplates, loadWorks]);

  // 读取详情页"做同款/再次编辑"传递的回显数据
  useEffect(() => {
    try {
      const raw = localStorage.getItem("ai_video_detail_apply");
      if (!raw) return;
      const data = JSON.parse(raw) as {
        prompt?: string;
        ratio?: string;
        mode?: string;
        generationMode?: string;
        resolution?: string;
        duration?: string;
        source?: string;
        inputFiles?: AiVideo.InputFile[];
      };
      // 回显提示词
      if (data.prompt) setPrompt(data.prompt);
      // 回显比例
      if (data.ratio) {
        setRatio(data.ratio);
        setRatioLocked(false);
      }
      // 回显分辨率
      if (data.resolution) setResolution(data.resolution);
      // 回显时长（形如 "5 秒" → 5）
      if (data.duration) {
        const num = parseInt(data.duration, 10);
        if (!Number.isNaN(num) && num > 0) setDuration(num);
      }
      // 回显生成模式：优先用后端原始 generationMode（如 "FIRST_FRAME"）经 API_TO_MODE 映射
      // 兜底兼容旧版中文标签 mode（反查 MODE_LABELS）
      let modeKey: Mode | undefined;
      if (data.generationMode) {
        modeKey = API_TO_MODE[data.generationMode] || REF_TYPE_TO_MODE[data.generationMode];
      } else if (data.mode) {
        const modeEntry = Object.entries(MODE_LABELS).find(
          ([, label]) => label === data.mode,
        );
        if (modeEntry) modeKey = modeEntry[0] as Mode;
      }
      if (modeKey) applyMode(modeKey);
      // 回填后端返回的输入素材到远程素材状态
      // 按 usage/category 分类到 first/last/referenceImage/Video/Audio
      if (data.inputFiles && data.inputFiles.length > 0) {
        setFiles({
          first: [],
          last: [],
          referenceImage: [],
          referenceVideo: [],
          referenceAudio: [],
        });
        setRemoteFiles(categorizeInputFiles(data.inputFiles));
        // 触发积分预估刷新
        setPointDirty((v) => v + 1);
      }
      // 清理一次性数据，避免下次进入仍覆盖
      localStorage.removeItem("ai_video_detail_apply");
      notify(
        `已载入${data.source === "work" ? "作品" : "模板"}参数${
          data.inputFiles && data.inputFiles.length > 0
            ? `与 ${data.inputFiles.length} 个素材`
            : ""
        }，可继续修改`,
      );
    } catch {
      /* localStorage 读取失败忽略 */
    }
    // 仅在组件首次挂载时执行一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 组件卸载时清理所有定时器并终止轮询
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
      if (generateTimerRef.current)
        window.clearTimeout(generateTimerRef.current);
      if (pointDebounceRef.current)
        window.clearTimeout(pointDebounceRef.current);
      if (focusTimerRef.current) window.clearTimeout(focusTimerRef.current);
      stopPolling();
    };
  }, [stopPolling]);

  // ====== 派生数据 ======

  /** 各分类下模板数量统计 */
  const filterCounts = useMemo<Record<TemplateFilter, number>>(() => {
    const counts: Record<TemplateFilter, number> = {
      all: templates.length,
      cinematic: 0,
      landscape: 0,
      portrait: 0,
      product: 0,
    };
    templates.forEach((t) => {
      counts[t.category] += 1;
    });
    return counts;
  }, [templates]);

  /** 按当前分类筛选后的模板列表 */
  const filteredTemplates = useMemo(() => {
    if (activeTemplateFilter === "all") return templates;
    return templates.filter((t) => t.category === activeTemplateFilter);
  }, [templates, activeTemplateFilter]);

  /**
   * 把模板分配到 N 列（masonry 瀑布流，与作品列表算法一致）
   * 按原始顺序分配到当前最矮列，保持顺序 + 交叉排列无空白
   */
  const templateColumns = useMemo(() => {
    const count = TEMPLATE_COLUMN_COUNT;
    if (filteredTemplates.length === 0) return [];
    const columns: TemplateItem[][] = Array.from({ length: count }, () => []);
    const scores = Array(count).fill(0);
    filteredTemplates.forEach((tpl) => {
      const target = scores.indexOf(Math.min(...scores));
      columns[target].push(tpl);
      scores[target] += templateHeightScore(tpl.ratio);
    });
    return columns;
  }, [filteredTemplates]);

  /**
   * 把作品分配到 N 列（masonry 瀑布流）
   * 按原始顺序（创建时间）分配到当前最矮列，保持时间顺序 + 交叉排列无空白
   */
  const workColumns = useMemo(() => {
    const count = TEMPLATE_COLUMN_COUNT;
    if (works.length === 0) return [];
    const columns: WorkItem[][] = Array.from({ length: count }, () => []);
    const scores = Array(count).fill(0);
    works.forEach((w) => {
      const target = scores.indexOf(Math.min(...scores));
      columns[target].push(w);
      scores[target] += templateHeightScore(w.ratio);
    });
    return columns;
  }, [works]);

  /**
   * 清理 masonry 列残留的拉伸样式
   * 早期版本会把矮列 minHeight 拉伸到最高列，并在 card-body 用 flex:1 撑满、
   * 多卡片列用 padding-bottom 分配高度差，这会在 1:1、21:9、自适应等比例下
   * 产生底部大空白。现已改为让瀑布流按卡片自然高度展示（卡片间仅 16px gap），
   * 此处仅保留清理逻辑，移除历史残留的内联样式，避免升级后旧样式残留。
   */
  useEffect(() => {
    if (activeGallery !== "templates") return;
    const items = filteredTemplates;
    if (items.length === 0) return;
    const grid = videoGridRef.current;
    if (!grid) return;
    const raf = requestAnimationFrame(() => {
      const columns = Array.from(
        grid.querySelectorAll<HTMLDivElement>(".template-column"),
      );
      if (columns.length === 0) return;
      // 清空历史残留的拉伸内联样式（minHeight / padding-bottom / filter-stretch）
      columns.forEach((column) => {
        column.style.height = "";
        Array.from(column.children).forEach((card) => {
          const el = card as HTMLElement;
          el.style.minHeight = "";
          el.classList.remove("filter-stretch");
          const body = el.querySelector<HTMLElement>(".card-body");
          if (body) body.style.paddingBottom = "";
        });
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [activeGallery, filteredTemplates, templateColumns]);

  /** 各选择器可选项（model/resolution/duration 为模块级常量，ratio 依赖 mode 动态计算） */
  const modelOptions = MODEL_OPTIONS;
  const resolutionOptions = RESOLUTION_OPTIONS;
  const durationOptions = DURATION_OPTIONS;
  const ratioOptions = useMemo<SelectOption[]>(
    () => [
      { value: "adaptive", label: "自适应", hidden: mode === "text" },
      { value: "21:9", label: "21:9" },
      { value: "16:9", label: "16:9" },
      { value: "4:3", label: "4:3" },
      { value: "1:1", label: "1:1" },
      { value: "3:4", label: "3:4" },
      { value: "9:16", label: "9:16" },
    ],
    [mode],
  );

  /** 当前比例显示文案 */
  const ratioLabel = ratio === "adaptive" ? "自适应" : ratio;
  /** 当前时长显示文案 */
  const durationLabel = `${duration} 秒`;

  /** 画廊底部说明文案 */
  const galleryNote =
    activeGallery === "templates"
      ? "点击模板查看详情 · “做同款”载入参数"
      : "任务自动保存 · 生成完成后可预览与下载";

  return (
    <div className="ai-video-page">
      <div className="video-shell">
        {/* 顶部标题区 */}
        <section className="studio-hero">
          <div className="section-kicker">Video Studio</div>
          <h1>AI 视频</h1>
          <p>一句话构建镜头，让静态想象成为流动叙事</p>
        </section>

        {/* 生成控制台 */}
        <section
          className="composer"
          aria-label="AI 视频生成控制台"
          ref={composerRef}
        >
          <div className="composer-head">
            <div className="model-lock">
              <span className="model-mark">
                <Clapperboard />
              </span>
              <span>视频生成</span>
            </div>
            {/* <span className="api-state">接口参数已同步</span> */}
          </div>

          {/* 模式切换 */}
          <div className="mode-tabs" role="tablist" aria-label="视频生成模式">
            {MODE_TABS.map((tab) => (
              <button
                key={tab.mode}
                type="button"
                role="tab"
                aria-selected={mode === tab.mode}
                className={`mode-tab ${mode === tab.mode ? "active" : ""}`}
                onClick={() => applyMode(tab.mode)}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* 首帧 / 尾帧上传面板 */}
          <div className={`media-panel ${isFrameMode ? "show" : ""}`}>
            <div className="media-grid">
              {(mode === "first" || mode === "first_last") && (
                <MediaSlot
                  icon={<ImageUp />}
                  title="上传首帧图片"
                  hint={buildSlotHint("first", "JPG、PNG、WEBP、HEIC · ≤30MB")}
                  count={files.first.length}
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                  multiple={false}
                  inputRef={firstFrameInputRef}
                  onChange={(selected) =>
                    handleFileChange(
                      "first",
                      selected,
                      1,
                      30,
                      "JPG、PNG、WEBP、HEIC · ≤30MB",
                    )
                  }
                  remoteFiles={remoteFiles.first}
                />
              )}
              {(mode === "last" || mode === "first_last") && (
                <MediaSlot
                  icon={<ImageDown />}
                  title="上传尾帧图片"
                  hint={buildSlotHint("last", "JPG、PNG、WEBP、HEIC · ≤30MB")}
                  count={files.last.length}
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                  multiple={false}
                  inputRef={lastFrameInputRef}
                  onChange={(selected) =>
                    handleFileChange(
                      "last",
                      selected,
                      1,
                      30,
                      "JPG、PNG、WEBP、HEIC · ≤30MB",
                    )
                  }
                  remoteFiles={remoteFiles.last}
                />
              )}
            </div>
            <p className="media-hint">
              输出比例将自动跟随输入图片，不能与多模态参考素材混用。
            </p>
          </div>

          {/* 多模态参考素材面板 */}
          <div className={`media-panel ${mode === "reference" ? "show" : ""}`}>
            <div className="media-grid reference-grid">
              <MediaSlot
                icon={<Images />}
                title="参考图片"
                hint={buildSlotHint("referenceImage", "最多 9 张 · 单张 ≤30MB")}
                count={files.referenceImage.length}
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                multiple
                inputRef={referenceImageInputRef}
                onChange={(selected) =>
                  handleFileChange(
                    "referenceImage",
                    selected,
                    9,
                    30,
                    "最多 9 张 · 单张 ≤30MB",
                  )
                }
                remoteFiles={remoteFiles.referenceImage}
              />
              <MediaSlot
                icon={<Film />}
                title="参考视频"
                hint={buildSlotHint(
                  "referenceVideo",
                  "最多 3 个 · 总时长 ≤15秒",
                )}
                count={files.referenceVideo.length}
                accept="video/mp4,video/quicktime"
                multiple
                inputRef={referenceVideoInputRef}
                onChange={(selected) =>
                  handleFileChange(
                    "referenceVideo",
                    selected,
                    3,
                    50,
                    "最多 3 个 · 总时长 ≤15秒",
                  )
                }
                remoteFiles={remoteFiles.referenceVideo}
              />
              <MediaSlot
                icon={<AudioLines />}
                title="参考音频"
                hint={buildSlotHint(
                  "referenceAudio",
                  "最多 3 个 · 不能单独使用",
                )}
                count={files.referenceAudio.length}
                accept="audio/wav,audio/mpeg"
                multiple
                inputRef={referenceAudioInputRef}
                onChange={(selected) =>
                  handleFileChange(
                    "referenceAudio",
                    selected,
                    3,
                    15,
                    "最多 3 个 · 不能单独使用",
                  )
                }
                remoteFiles={remoteFiles.referenceAudio}
              />
            </div>
            <p className="media-hint">
              参考素材用于保持人物、动作、镜头或音色一致；音频必须与参考图片或视频一起使用。
            </p>
          </div>

          {/* 提示词输入区 */}
          <div className="composer-main">
            <div className="prompt-label">
              <span>视频描述</span>
            </div>
            <div className="prompt-wrap">
              <textarea
                ref={promptRef}
                className="prompt"
                maxLength={PROMPT_MAX_LENGTH}
                aria-label="视频提示词"
                placeholder="描述场景、人物、动作、镜头运动、光影与氛围…"
                value={prompt}
                onChange={(event) => setPrompt(event.target.value)}
              />
              <div className="prompt-count">
                {prompt.length}/{PROMPT_MAX_LENGTH}
              </div>
            </div>
          </div>

          {/* 工具栏：参数选择器 + 生成按钮 */}
          <div className="composer-toolbar">
            <div className="selectors" aria-label="视频生成参数">
              <SelectDropdown
                icon={<Cpu />}
                label={model}
                options={modelOptions}
                open={openMenu === "model"}
                onTriggerClick={() => handleToggleMenu("model")}
                onOptionSelect={(value) => handleSelectOption("model", value)}
              />
              <SelectDropdown
                icon={<Scan />}
                label={resolution}
                options={resolutionOptions}
                open={openMenu === "resolution"}
                onTriggerClick={() => handleToggleMenu("resolution")}
                onOptionSelect={(value) =>
                  handleSelectOption("resolution", value)
                }
              />
              <SelectDropdown
                icon={<Clock3 />}
                label={durationLabel}
                options={durationOptions}
                open={openMenu === "duration"}
                onTriggerClick={() => handleToggleMenu("duration")}
                onOptionSelect={(value) =>
                  handleSelectOption("duration", value)
                }
              />
              <SelectDropdown
                icon={<RectangleHorizontal />}
                label={ratioLabel}
                options={ratioOptions}
                open={openMenu === "ratio"}
                disabled={ratioLocked}
                title={ratioLocked ? "图生视频比例由输入图片自动决定" : ""}
                onTriggerClick={() => handleToggleMenu("ratio")}
                onOptionSelect={(value) => handleSelectOption("ratio", value)}
              />
              {/* <label className="watermark-toggle" title="在生成视频中添加 AIGC 标识">
                  <input
                    type="checkbox"
                    checked={aigcWatermark}
                    onChange={(event) => setAigcWatermark(event.target.checked)}
                  />
                  <span>AIGC 水印</span>
                </label> */}
            </div>
            <div className="actions">
              <span
                className={`cost-pill ${
                  prompt.trim() && (pointLoading || estimatedPoint !== null)
                    ? ""
                    : "is-hidden"
                }`}
                title="按当前参数预估，实际以扣减为准"
              >
                <CoinsEstimateIcon />
                {pointLoading ? (
                  <span>积分预估中…</span>
                ) : estimatedPoint !== null ? (
                  <span>
                    预计消耗 <em className="cost-point">{estimatedPoint}</em>{" "}
                    积分
                  </span>
                ) : null}
              </span>
              <button
                type="button"
                className={`generate-button ${generating ? "is-spinning" : ""}`}
                aria-label="生成视频"
                title="生成视频"
                disabled={generating}
                onClick={handleGenerate}
              >
                {generating ? <Loader2 /> : <ArrowRight />}
              </button>
            </div>
          </div>
        </section>

        {/* 灵感卡片 */}
        <section className="inspiration-block" aria-labelledby="starterTitle">
          <header className="inspiration-header">
            <div>
              <span className="inspiration-kicker">Prompt Starters</span>
              <h2 id="starterTitle">从一个镜头开始</h2>
            </div>
            <p className="inspiration-hint">
              点击灵感卡片，快速填入完整视频描述
            </p>
          </header>
          <div className="inspiration">
            {IDEA_CARDS.map((idea, idx) => (
              <IdeaCardView
                key={idea.title}
                idea={idea}
                index={idx}
                onClick={handleIdeaClick}
              />
            ))}
          </div>
        </section>

        {/* 模板 / 作品画廊 */}
        <section
          className="works-section"
          aria-labelledby="galleryTitle"
          ref={worksSectionRef}
        >
          <header className="works-toolbar">
            <div className="tabs" id="galleryTitle">
              <button
                type="button"
                role="tab"
                aria-selected={activeGallery === "templates"}
                className={`tab ${activeGallery === "templates" ? "active" : ""}`}
                onClick={() => {
                  setActiveGallery("templates");
                  try {
                    sessionStorage.setItem(
                      "ai_video_active_gallery",
                      "templates",
                    );
                  } catch {
                    /* sessionStorage 不可用时忽略 */
                  }
                }}
              >
                模板
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeGallery === "works"}
                className={`tab ${activeGallery === "works" ? "active" : ""}`}
                onClick={() => {
                  setActiveGallery("works");
                  try {
                    sessionStorage.setItem("ai_video_active_gallery", "works");
                  } catch {
                    /* sessionStorage 不可用时忽略 */
                  }
                }}
              >
                我的作品
              </button>
            </div>
            <span className="works-note">{galleryNote}</span>
          </header>

          <div ref={videoGridRef} className="video-grid template-view">
            {activeGallery === "templates" ? (
              templatesLoading ? (
                <div className="empty-state">模板加载中…</div>
              ) : filteredTemplates.length === 0 ? (
                <div className="empty-state">暂无模板</div>
              ) : (
                templateColumns.map((column, colIdx) => (
                  <div className="template-column" key={colIdx}>
                    {column.map((template) => (
                      <TemplateCardView
                        key={template.templateId}
                        template={template}
                        onUse={handleUseTemplate}
                        onView={handleViewTemplate}
                      />
                    ))}
                  </div>
                ))
              )
            ) : worksLoading ? (
              <div className="empty-state">作品加载中…</div>
            ) : (
              workColumns.map((column, colIdx) => (
                <div className="template-column" key={colIdx}>
                  {column.map((work) => (
                    <WorkCardView
                      key={work.id}
                      work={work}
                      onView={handleViewWork}
                      onDelete={handleDeleteWork}
                      onReuse={handleReuseWork}
                      onDownload={handleDownloadWork}
                    />
                  ))}
                </div>
              ))
            )}
            {/*
           
              始终存在于 videoGrid 内部最后一个位置，通过 is-hidden 控制显隐。
              仅当"我的视频"tab + 无作品 + 非加载中时显示。
            */}
            <div
              className={`empty-state${
                activeGallery !== "works" || works.length > 0 || worksLoading
                  ? " is-hidden"
                  : ""
              }`}
              data-kind="empty"
            >
              还没有生成视频。在上方完成参数设置，创建你的第一个视频。
            </div>
          </div>
        </section>
      </div>

      {/* Toast 提示（使用 AppToast 组件，与 AI 生图一致） */}
      <AppToast
        config={
          toast.visible
            ? { type: "info", message: toast.text, closable: false }
            : null
        }
        onClose={() => setToast({ visible: false, text: "" })}
      />
    </div>
  );
};
